/**
 * LCP (Largest Contentful Paint) Preloader
 *
 * Optimized image preloading to improve Core Web Vitals, specifically LCP.
 * Uses multiple preloading strategies and intelligent caching.
 */

interface PreloadOptions {
  priority?: 'high' | 'low';
  as?: 'image' | 'fetch';
  crossorigin?: 'anonymous' | 'use-credentials';
}

class LCPPreloader {
  private preloadedUrls = new Set<string>();
  private preloadLinks = new Map<string, HTMLLinkElement>();
  private preloadDebounceTimer: NodeJS.Timeout | null = null;

  /**
   * Preload an image using link preload for optimal performance
   */
  preloadImage(url: string, options: PreloadOptions = {}) {
    // Skip if already preloaded
    if (this.preloadedUrls.has(url)) {
      return;
    }

    // Check if preload already exists in DOM from another source
    const existingPreload = document.querySelector(`link[rel="preload"][href="${url}"]`);
    if (existingPreload) {
      this.preloadedUrls.add(url);
      return;
    }

    try {
      // Use only link preload strategy for best performance and browser support
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = options.as || 'image';
      link.href = url;

      if (options.crossorigin) {
        link.crossOrigin = options.crossorigin;
      }

      // Add fetchpriority for modern browsers
      if (options.priority) {
        (link as any).fetchPriority = options.priority;
      }

      // Add error handling
      link.onerror = () => {
        console.warn(`Image preload failed: ${url}`);
        // Try fallback URL if this was a thumbnail
        this.tryFallbackImage(url, options);
      };

      document.head.appendChild(link);

      this.preloadedUrls.add(url);
      this.preloadLinks.set(url, link);
    } catch (error) {
      console.error('Error preloading image:', error);
    }
  }

  /**
   * Try fallback image formats when the primary image fails
   */
  private tryFallbackImage(failedUrl: string, options: PreloadOptions = {}) {
    // Don't retry if we've already tried fallbacks
    if (failedUrl.includes('-fallback') || this.preloadedUrls.has(`${failedUrl  }-fallback`)) {
      return;
    }

    let fallbackUrl: string | null = null;

    // Convert WebP to JPEG fallback
    if (failedUrl.includes('.webp')) {
      fallbackUrl = failedUrl.replace('.webp', '.jpg');
    }
    // Convert AVIF to JPEG fallback
    else if (failedUrl.includes('.avif')) {
      fallbackUrl = failedUrl.replace('.avif', '.jpg');
    }
    // Try medium thumbnail if large thumbnail fails
    else if (failedUrl.includes('-large.')) {
      fallbackUrl = failedUrl.replace('-large.', '-medium.');
    }
    // Try small thumbnail if medium thumbnail fails
    else if (failedUrl.includes('-medium.')) {
      fallbackUrl = failedUrl.replace('-medium.', '-small.');
    }

    if (fallbackUrl && fallbackUrl !== failedUrl) {
      // Mark this as a fallback attempt
      this.preloadedUrls.add(`${failedUrl  }-fallback`);
      this.preloadImage(fallbackUrl, { ...options, priority: 'low' });
    }
  }

  /**
   * Preload multiple images
   */
  preloadImages(urls: string[], options: PreloadOptions = {}) {
    urls.forEach((url) => this.preloadImage(url, options));
  }

  /**
   * Preload images based on route pattern with debouncing
   */
  async preloadForRoute(pathname: string) {
    // Clear any pending preload operations
    if (this.preloadDebounceTimer) {
      clearTimeout(this.preloadDebounceTimer);
      this.preloadDebounceTimer = null;
    }

    // Debounce preloading to avoid excessive operations during rapid navigation
    this.preloadDebounceTimer = setTimeout(async () => {
      if (pathname === '/') {
        await this.preloadHomePageImages();
      } else if (pathname.startsWith('/deals') && pathname !== '/deals/') {
        // Individual deal page
        const dealId = pathname.split('/deals/')[1];
        if (dealId) {
          await this.preloadDealDetailImage(dealId);
        }
      } else if (['/deals', '/trending', '/search'].includes(pathname)) {
        await this.preloadFirstDealImage();
      }
    }, 50); // 50ms debounce
  }

  /**
   * Preload images likely to be LCP on home page
   * Strategy: Use cached data from Home component if available, otherwise make minimal request
   */
  private async preloadHomePageImages() {
    try {
      let deals = [];
      let dataSource = 'unknown';

      // Strategy 1: Try to use cached data from Home component's larger request (limit=24)
      try {
        const { getDeals } = await import('../services/dealService');
        const homeData = await getDeals(1, 24, '-createdAt');
        deals = homeData.deals || homeData;
        dataSource = 'cache-from-home';
      } catch (cacheError) {
        // Strategy 2: Cache miss, make our own smaller request as fallback
        const { getDeals } = await import('../services/dealService');
        const data = await getDeals(1, 4, '-createdAt');
        deals = data.deals || data;
      }

      if (deals && Array.isArray(deals)) {
        const { getApiUrl } = await import('./apiUrlResolver');
        const baseUrl = getApiUrl().replace('/api', ''); // Remove /api to get server base URL

        deals.slice(0, 2).forEach((deal: any, index: number) => {
          const priority = index === 0 ? 'high' : 'low';

          // Preload only the primary image format to reduce network congestion
          if (deal.thumbnail) {
            // Handle relative URLs
            const thumbnailUrl = deal.thumbnail.startsWith('/')
              ? `${baseUrl}${deal.thumbnail}`
              : deal.thumbnail;

            // Only preload the primary thumbnail, not multiple formats
            this.preloadImage(thumbnailUrl, { priority });
          }
        });

        // Preload a fallback image as well
        this.preloadImage('/images/logos/deals-n-steals-logo-optimized.svg', { priority: 'low' });
      }
    } catch (error) {
      console.warn('Could not preload home page images:', error);
      // Fallback: preload brand logo as placeholder
      this.preloadImage('/images/logos/deals-n-steals-logo-optimized.svg', { priority: 'low' });
    }
  }

  /**
   * Preload first deal image for listing pages
   */
  private async preloadFirstDealImage() {
    try {
      // USE CACHED REQUEST SYSTEM instead of direct fetch
      const { getDeals } = await import('../services/dealService');

      const data = await getDeals(1, 1);

      // Handle dealService response format
      const deals = data.deals || data;

      if (deals?.[0]) {
        const deal = deals[0];
        // Use the correct thumbnail structure: optimizedImages[0].thumbnails.medium
        if (deal.optimizedImages?.[0]?.thumbnails?.medium?.webp) {
          this.preloadImage(deal.optimizedImages[0].thumbnails.medium.webp, { priority: 'high' });
        } else if (deal.optimizedImages?.[0]?.thumbnails?.medium?.jpeg) {
          this.preloadImage(deal.optimizedImages[0].thumbnails.medium.jpeg, { priority: 'high' });
        } else if (deal.optimizedImages?.[0]?.webp) {
          this.preloadImage(deal.optimizedImages[0].webp, { priority: 'high' });
        } else if (deal.optimizedImages?.[0]?.jpeg) {
          this.preloadImage(deal.optimizedImages[0].jpeg, { priority: 'high' });
        } else if (deal.imageUrl) {
          this.preloadImage(deal.imageUrl, { priority: 'high' });
        }
      }
    } catch (error) {
      console.warn('Could not preload first deal image:', error);
    }
  }

  private async preloadDealDetailImage(dealId: string) {
    try {
      // USE CACHED REQUEST SYSTEM instead of direct fetch
      const { getDealById } = await import('../services/dealService');

      const deal = await getDealById(dealId);

      // getDealById returns the deal directly

      // Preload the main image (not thumbnail) for deal detail pages
      // This matches what ImageGallery actually uses
      if (deal.optimizedImages?.[0]?.webp) {
        this.preloadImage(deal.optimizedImages[0].webp, { priority: 'high' });
      } else if (deal.optimizedImages?.[0]?.jpeg) {
        this.preloadImage(deal.optimizedImages[0].jpeg, { priority: 'high' });
      } else if (deal.imageUrl) {
        this.preloadImage(deal.imageUrl, { priority: 'high' });
      }
    } catch (error) {
      console.warn('Could not preload deal detail image:', error);
    }
  }

  /**
   * Clean up preloaded resources
   */
  cleanup(url?: string) {
    if (url) {
      // Clean up specific URL
      const link = this.preloadLinks.get(url);
      if (link && document.head.contains(link)) {
        document.head.removeChild(link);
      }
      this.preloadedUrls.delete(url);
      this.preloadLinks.delete(url);
    } else {
      // Clean up all preloaded resources
      this.preloadLinks.forEach((link, url) => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      });
      this.preloadedUrls.clear();
      this.preloadLinks.clear();
    }
  }

  /**
   * Get preload statistics
   */
  getStats() {
    return {
      preloadedCount: this.preloadedUrls.size,
      preloadedUrls: Array.from(this.preloadedUrls),
    };
  }
}

// Export singleton instance
export const lcpPreloader = new LCPPreloader();

/**
 * Hook for using LCP preloader in React components
 */
export const useLCPPreloader = () => {
  return lcpPreloader;
};

export default lcpPreloader;
